import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { buildDisplayName, formatDisplayName } from '../../utils/nameFormatting';
import '../../styles/components/dashboard/UpcomingBookings.css';

const BOOKINGS_API = 'https://gp64xgt52i.execute-api.eu-west-2.amazonaws.com/dev/me/bookings?status=confirmed&upcoming=true&limit=5';
const DATE_OPTIONS = { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
const TIME_OPTIONS = { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hour12: false };

const bookingDates = (booking) => {
  const start = new Date(booking.startTime);
  const end = new Date(booking.endTime);
  // All-day intervals use an exclusive end, as in the existing calendar.
  const lastDay = booking.allDay && end > start ? new Date(end.getTime() - 1) : end;
  const firstDate = start.toLocaleDateString('en-GB', DATE_OPTIONS);
  const lastDate = lastDay.toLocaleDateString('en-GB', DATE_OPTIONS);
  return {
    date: firstDate === lastDate ? firstDate : `${firstDate} – ${lastDate}`,
    time: booking.allDay ? 'All day' : `${start.toLocaleTimeString('en-GB', TIME_OPTIONS)} – ${end.toLocaleTimeString('en-GB', TIME_OPTIONS)}`,
  };
};

const UpcomingBookings = () => {
  const { authFetch } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const load = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await authFetch(BOOKINGS_API, { signal });
        if (!response.ok) throw new Error('Booking request failed');
        const payload = await response.json();
        if (!Array.isArray(payload.bookings) || payload.bookings.some((booking) =>
          !booking?.id || !Number.isFinite(Date.parse(booking.startTime)) ||
          !Number.isFinite(Date.parse(booking.endTime)) || Date.parse(booking.endTime) < Date.parse(booking.startTime)
        )) throw new Error('Invalid booking response');
        if (!signal.aborted) setBookings(payload.bookings.slice(0, 5));
      } catch {
        if (!signal.aborted) setError(true);
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [authFetch, attempt]);

  return (
    <section className="upcoming-bookings" aria-labelledby="upcoming-bookings-title">
      <header className="upcoming-bookings__header">
        <h2 id="upcoming-bookings-title">Upcoming bookings</h2>
        <p>Your next five confirmed bookings · Times shown in UK time</p>
      </header>

      {loading && <p className="upcoming-bookings__state" role="status">Loading bookings…</p>}
      {!loading && error && (
        <div className="upcoming-bookings__state" role="alert">
          <p>We could not load your upcoming bookings.</p>
          <button type="button" onClick={() => setAttempt((value) => value + 1)}>Try again</button>
        </div>
      )}
      {!loading && !error && bookings.length === 0 && (
        <p className="upcoming-bookings__state">No upcoming confirmed bookings.</p>
      )}
      {!loading && !error && bookings.length > 0 && (
        <ul className="upcoming-bookings__list">
          {bookings.map((booking) => {
            const dates = bookingDates(booking);
            const clientName = buildDisplayName(booking.client?.givenName, booking.client?.familyName)
              || formatDisplayName(booking.clientName) || 'Client';
            return (
              <li className="upcoming-bookings__item" key={booking.id}>
                <div className="upcoming-bookings__topline">
                  <h3>{booking.eventName || 'Booking'}</h3>
                  <span className="upcoming-bookings__status">Confirmed</span>
                </div>
                <p className="upcoming-bookings__client">{clientName}{booking.shootType && ` · ${booking.shootType}`}</p>
                <dl className="upcoming-bookings__details">
                  <div><dt>Date</dt><dd><time dateTime={booking.startTime}>{dates.date}</time></dd></div>
                  <div><dt>Time</dt><dd>{dates.time}</dd></div>
                  <div className="upcoming-bookings__location"><dt>Location</dt><dd>{booking.address || 'Not specified'}</dd></div>
                </dl>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default UpcomingBookings;
